import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Modal,
  Progress,
  Stack,
  Text,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import { TextInput, MultiSelect } from "react-hook-form-mantine";
import { notifications } from "@mantine/notifications";
import { web_checkInAttendeeSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { IconPrinter } from "@tabler/icons-react";

import {
  useGetTicket,
  useUpdateTicket,
  useGetCustomFields,
  useGetTicketColumns,
} from "../../api/tickets";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { renderBadgePdf, useGetBadgeTemplates } from "../../api/badges";
import { extractDirtyData } from "../../utils/dirtyData";
import { capitalize } from "../../utils/textUtils";
import { printTicket } from "../../utils/printTicket";
import { printPdfUrl } from "../../utils/printPdfUrl";
import defaultColors from "../Tags/utils/defaultColors";

const AUTO_PRINT_SECONDS = 3;

const RESERVED_CUSTOM_FIELD_COLS = [
  "picture",
  "firstName",
  "lastName",
  "email",
  "category",
  "checkedInAt",
];

const InvalidView = ({ onClose }) => (
  <Stack>
    <Alert color="red" title="Invalid ticket">
      This ticket is not valid for this event.
    </Alert>
    <Group justify="flex-end">
      <Button variant="default" onClick={onClose}>
        Close
      </Button>
    </Group>
  </Stack>
);

const StatusBanner = ({ alreadyCheckedIn, isPrintingEnabled }) =>
  alreadyCheckedIn ? (
    <Alert color="yellow" title="Checked in">
      {isPrintingEnabled ? "Re-print is allowed." : "Attendee is checked in."}
    </Alert>
  ) : (
    <Alert color="green" title="Ready to check in">
      {isPrintingEnabled
        ? "Confirm details and print the badge."
        : "Confirm the attendee details."}
    </Alert>
  );

const AttendeeHeader = ({ ticket, headerTags }) => (
  <Group wrap="nowrap">
    <Avatar
      src={ticket?.picture}
      size="lg"
      radius="sm"
      color="initials"
      name={`${ticket?.firstName || ""} ${ticket?.lastName || ""}`}
    />
    <Stack gap={2}>
      <Text fw={600}>
        {ticket?.firstName} {ticket?.lastName}
      </Text>
      <Text size="sm" c="dimmed">
        {ticket?.email}
      </Text>
      {headerTags.length > 0 && (
        <Group gap={4} mt={4}>
          {headerTags.map((tag) => (
            <Badge
              key={tag.id}
              variant="light"
              color={
                tag.color ||
                defaultColors.find((c) => c.subtype === tag.subtype)?.color
              }
            >
              {capitalize(tag.title)}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  </Group>
);

const EditableFields = ({ control, categoryTags, customColumns }) => (
  <>
    <Group grow>
      <TextInput label="First name" control={control} name="firstName" />
      <TextInput label="Last name" control={control} name="lastName" />
    </Group>
    <TextInput label="Email" control={control} name="email" />
    <MultiSelect
      label="Category"
      placeholder="Select category"
      control={control}
      name="categoryOfUser"
      data={(categoryTags || []).map((tag) => ({
        value: String(tag.id),
        label: capitalize(tag.title),
      }))}
    />

    {customColumns.length > 0 && (
      <>
        <Divider label="Custom fields" labelPosition="left" />
        {customColumns.map((field) => (
          <TextInput
            key={field.name}
            label={field.name}
            control={control}
            name={`customFields.${field.name}`}
          />
        ))}
      </>
    )}
  </>
);

const Actions = ({
  isPrintingEnabled,
  isSubmitting,
  onCancel,
  onSave,
  onPrint,
}) => (
  <Group justify="flex-end" gap="sm">
    <Button variant="default" onClick={onCancel} disabled={isSubmitting}>
      Cancel
    </Button>
    <Button
      variant={isPrintingEnabled ? "default" : "filled"}
      onClick={onSave}
      loading={isSubmitting}
    >
      Save
    </Button>
    {isPrintingEnabled && (
      <Button
        leftSection={<IconPrinter size={16} />}
        onClick={onPrint}
        loading={isSubmitting}
      >
        Print
      </Button>
    )}
  </Group>
);

const AutoPrintCountdown = ({ secondsLeft }) => (
  <Stack gap={6} mt="sm">
    <Progress
      value={((AUTO_PRINT_SECONDS - secondsLeft) / AUTO_PRINT_SECONDS) * 100}
      transitionDuration={900}
    />
    <Text ta="center" fw={500}>
      {secondsLeft > 0 ? `Printing in ${secondsLeft}..` : "Printing.."}
    </Text>
    <Text ta="center" size="xs" c="dimmed">
      Click anywhere to cancel and edit details.
    </Text>
  </Stack>
);

const AttendeeScanModal = ({
  ticketId,
  eventId,
  alreadyCheckedIn: alreadyCheckedInProp,
  isPrintingEnabled = true,
  isAutoPrintEnabled = false,
  // Attendees scanned at the station while this modal is open.
  queuedCount = 0,
  onClose,
}) => {
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoPrintCanceled, setAutoPrintCanceled] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(AUTO_PRINT_SECONDS);

  const {
    data: ticket,
    isPending: ticketPending,
    isError: ticketError,
  } = useGetTicket({ ticketId });
  const { data: badgeTemplates } = useGetBadgeTemplates({ eventId });
  const defaultBadgeId = badgeTemplates?.find((b) => b.isDefault)?.id;
  const { data: customFieldsData } = useGetCustomFields({ eventId, ticketId });
  const { data: columns } = useGetTicketColumns({ eventId });
  const { data: categoryTags } = useGetPeopleTagsBySubtype({
    eventId,
    subtype: "categoryOfUser",
  });

  const updateTicket = useUpdateTicket({ ticketId });

  const values = useMemo(() => {
    if (!ticket) return undefined;
    const customFields = {};
    (customFieldsData || []).forEach((f) => {
      customFields[f.name] = f.value || "";
    });
    return {
      firstName: ticket.firstName || "",
      lastName: ticket.lastName || "",
      email: ticket.email || "",
      categoryOfUser: (ticket.tags || [])
        .filter((t) => t.subtype === "categoryOfUser")
        .map((t) => String(t.id)),
      customFields,
    };
  }, [ticket, customFieldsData]);

  const {
    control,
    handleSubmit,
    setError,
    formState: { dirtyFields },
  } = useForm({
    values,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      categoryOfUser: [],
      customFields: {},
    },
    resolver: resolver(web_checkInAttendeeSchema),
  });

  const isWrongEvent = ticket && (!eventId || ticket.eventId !== eventId);
  const isInvalid = ticketError || isWrongEvent;
  // The print station passes whether the ticket was already checked in *before*
  // this scan (the scan itself flips checkedInAt). Fall back to the ticket when
  // no override is given (e.g. opening the modal outside the scan flow).
  const alreadyCheckedIn = alreadyCheckedInProp ?? !!ticket?.checkedInAt;
  const loading = ticketPending;

  const customColumns = (columns || []).filter(
    (col) => col.isVisible && !RESERVED_CUSTOM_FIELD_COLS.includes(col.name)
  );
  const headerTags = (ticket?.tags || []).filter(
    (t) => t.subtype === "categoryOfUser"
  );

  const closeTimeoutRef = useRef(null);
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    []
  );

  const printNameTicket = useCallback(
    (formData) =>
      printTicket({
        firstName: formData.firstName || ticket?.firstName,
        lastName: formData.lastName || ticket?.lastName,
      }),
    [ticket?.firstName, ticket?.lastName]
  );

  const printBadge = useCallback(
    async (formData) => {
      if (defaultBadgeId) {
        try {
          const { url } = await renderBadgePdf({
            badgeId: defaultBadgeId,
            ticketId,
          });
          await printPdfUrl(url);
          return;
        } catch (err) {
          printNameTicket(formData);
          return;
        }
      }
      printNameTicket(formData);
    },
    [defaultBadgeId, ticketId, printNameTicket]
  );

  const finishWithSuccess = useCallback(
    (title) => {
      notifications.show({ title, color: "green" });
      closeTimeoutRef.current = setTimeout(onClose, 1500);
    },
    [onClose]
  );

  // Persist any edited fields, then optionally print. Check-in itself
  // (ticket.checkedInAt + ticketHistory) is owned by the scan endpoint, so the
  // modal never writes it.
  const saveAttendee = async (formData, shouldPrint) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const ticketData = {};
      if (dirtyFields.firstName && formData.firstName) {
        ticketData.firstName = formData.firstName;
      }
      if (dirtyFields.lastName && formData.lastName) {
        ticketData.lastName = formData.lastName;
      }
      if (dirtyFields.email && formData.email) {
        ticketData.email = formData.email;
      }
      if (dirtyFields.categoryOfUser) {
        ticketData.categoryOfUser = formData.categoryOfUser;
      }
      if (dirtyFields.customFields) {
        const dirty = extractDirtyData(
          formData.customFields,
          dirtyFields.customFields
        );
        if (Object.keys(dirty).length) ticketData.customFields = dirty;
      }
      if (Object.keys(ticketData).length) {
        await updateTicket.mutateAsync(ticketData);
      }

      if (shouldPrint) {
        try {
          await printBadge(formData);
          finishWithSuccess(
            alreadyCheckedIn ? "Saved & printed" : "Checked in & printed"
          );
        } catch (printErr) {
          notifications.show({
            title: alreadyCheckedIn ? "Saved" : "Checked in",
            message: "Badge could not be printed. Retry from the table.",
            color: "yellow",
          });
          closeTimeoutRef.current = setTimeout(onClose, 1500);
        }
      } else {
        finishWithSuccess("Saved");
      }
    } catch (err) {
      setSubmitError(err?.message || "Something went wrong. Please retry.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelAutoPrint = () => setAutoPrintCanceled(true);
  const handleSave = handleSubmit(
    (formData) => saveAttendee(formData, false),
    cancelAutoPrint
  );
  const handleSaveAndPrint = handleSubmit(
    (formData) => saveAttendee(formData, true),
    cancelAutoPrint
  );
  const handlePrimaryAction = isPrintingEnabled
    ? handleSaveAndPrint
    : handleSave;

  // Attendee fields that must be filled before we auto-print. firstName/lastName
  // pass the schema even when empty, so they're checked here too.
  const missingFields = useMemo(() => {
    if (!values) return [];
    const missing = [];
    if (!values.firstName.trim()) missing.push("firstName");
    if (!values.lastName.trim()) missing.push("lastName");
    if (!values.categoryOfUser.length) missing.push("categoryOfUser");
    return missing;
  }, [values]);

  // Highlight the empty fields so the admin fills them in (they can still print
  // manually). missingFields comes from the loaded ticket, not live edits, so
  // this runs once.
  useEffect(() => {
    missingFields.forEach((name) =>
      setError(name, { type: "required", message: "Required to print" })
    );
  }, [missingFields, setError]);

  // Auto-print countdown: on a scan with auto-print on and no missing fields, count
  // down and then submit as if the admin clicked "Print" (print preview is
  // skipped by the kiosk flag). Any click in the modal cancels it and returns
  // manual control.
  const autoPrintActive =
    isPrintingEnabled &&
    isAutoPrintEnabled &&
    !loading &&
    !isInvalid &&
    !autoPrintCanceled &&
    !submitError &&
    !missingFields.length;

  const savePrintRef = useRef(handleSaveAndPrint);
  savePrintRef.current = handleSaveAndPrint;

  useEffect(() => {
    if (!autoPrintActive) return undefined;
    if (secondsLeft <= 0) {
      savePrintRef.current();
      return undefined;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [autoPrintActive, secondsLeft]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        if (e.target?.tagName === "TEXTAREA") return;
        e.preventDefault();
        if (isSubmitting || loading || isInvalid) return;
        setAutoPrintCanceled(true);
        handlePrimaryAction();
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (!isSubmitting) {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handlePrimaryAction, onClose, isSubmitting, loading, isInvalid]);

  return (
    <Modal
      opened
      onClose={isSubmitting ? () => {} : onClose}
      title={
        <Group gap="xs">
          <Text fw={600}>Attendee check-in</Text>
          {queuedCount > 0 && (
            <Badge variant="light">{queuedCount} in queue</Badge>
          )}
        </Group>
      }
      size="lg"
      centered
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      {loading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}

      {!loading && isInvalid && <InvalidView onClose={onClose} />}

      {!loading && !isInvalid && (
        <form
          onSubmit={handlePrimaryAction}
          onClick={() => autoPrintActive && setAutoPrintCanceled(true)}
        >
          <Stack>
            <StatusBanner
              alreadyCheckedIn={alreadyCheckedIn}
              isPrintingEnabled={isPrintingEnabled}
            />
            <AttendeeHeader ticket={ticket} headerTags={headerTags} />
            <Divider />
            <EditableFields
              control={control}
              categoryTags={categoryTags}
              customColumns={customColumns}
            />
            {submitError && (
              <Alert color="red" title="Could not save">
                {submitError}
              </Alert>
            )}
            {autoPrintActive ? (
              <AutoPrintCountdown secondsLeft={secondsLeft} />
            ) : (
              <Actions
                isPrintingEnabled={isPrintingEnabled}
                isSubmitting={isSubmitting}
                onCancel={onClose}
                onSave={handleSave}
                onPrint={handleSaveAndPrint}
              />
            )}
          </Stack>
        </form>
      )}
    </Modal>
  );
};

export default AttendeeScanModal;
